import { useSearchParams } from "react-router-dom";
import { ListQueryParams, CreateCompanyInput, UpdateCompanyInput } from "../api/crmApi";
import {
    useCompaniesQuery,
    useCompany,
    useCreateCompany,
    useUpdateCompany,
    useDeleteCompany,
} from "../query/companies";

export function useCompanies() {
    const [searchParams, setSearchParams] = useSearchParams();

    const params: ListQueryParams = {
        page: Number(searchParams.get("page")) || 1,
        limit: Number(searchParams.get("limit")) || 20,
        search: searchParams.get("search") || undefined,
        sortBy: searchParams.get("sortBy") || undefined,
        sortOrder: (searchParams.get("sortOrder") as "asc" | "desc") || undefined,
        status: searchParams.get("status") || undefined,
        industry: searchParams.get("industry") || undefined,
    };

    const query = useCompaniesQuery(params);
    const createMutation = useCreateCompany();
    const updateMutation = useUpdateCompany();
    const deleteMutation = useDeleteCompany();

    const updateQuery = (updates: Partial<ListQueryParams & { pageSize?: number }>) => {
        const next = new URLSearchParams(searchParams);
        const isFilterChange = Object.keys(updates).some(
            (k) => k !== "page" && k !== "pageSize" && k !== "limit",
        );
        if (isFilterChange) {
            next.set("page", "1");
        }
        Object.entries(updates).forEach(([key, value]) => {
            const urlKey = key === "pageSize" ? "limit" : key;
            if (value === undefined || value === null || value === "") {
                next.delete(urlKey);
            } else {
                next.set(urlKey, String(value));
            }
        });
        setSearchParams(next, { replace: true });
    };

    return {
        companies: query.data?.data ?? [],
        total: query.data?.total ?? 0,
        totalPages: query.data?.totalPages ?? 0,
        currentPage: params.page ?? 1,
        pageSize: params.limit ?? 20,
        loading: query.isLoading,
        error: query.error,

        updateQuery,

        addCompany: (data: CreateCompanyInput) => createMutation.mutateAsync(data),
        editCompany: (id: string, payload: UpdateCompanyInput) =>
            updateMutation.mutateAsync({ id, payload }),
        removeCompany: (id: string) => deleteMutation.mutateAsync(id),

        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
    };
}

export function useCompanyDetail(id?: string) {
    const result = useCompany(id ?? "");
    return {
        company: result.data ?? null,
        loading: result.isLoading,
        error: result.error,
        reload: () => result.refetch(),
    };
}
